import { Component, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { authPasswordFlowConfig } from "./auth-password-flow.config";
import { OAuthService } from "angular-oauth2-oidc";
import { AlertService } from "./alert-panel/alert.service";
import { AlertPanelModel } from "./_models/common/alertPanelModel";
import { UpToDateBuildService } from "./up-to-date.service";
import { NavigationEnd, Router } from "@angular/router";
import { filter, from, interval, switchMap } from "rxjs";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { CreditCardService } from "./_services/credit-card/credit-card.service";
import { ToastrService } from "ngx-toastr";
import { ClaimsService } from "./_services/common/claims.service";
import { Claims } from "./_models/common/claims";
import { DragnDropCardImportResponse, CreditCardStatementTransaction, ReadCreditCardStatementResponse } from "./_models/common/card-review-response";
import { DatePipe } from "@angular/common";
import { CardImportTemplateSaveRequest, ICardImportTemplateSaveRequest } from "./_models/credit-card/credit-card";
import { ApiRequest } from "./_models";
import { environment } from "src/environments/environment";
import { ColDef } from "ag-grid-community";
import { GridConfig } from "./_models/common/grid/grid-config";
import { GridComponent } from "./shared/AG-grid/grid-component/grid.component";
import { ICONS } from "./_helpers/icons.constants";
export let browserRefresh = false;
declare global {
  interface Window {
    tidioChatApi: any;
  }
}

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  @ViewChild('reviewGridRef') reviewGridRef: GridComponent;

  private hideOnRoutes = [];
  claims: Claims;
  dragnDropCardImportResponse: DragnDropCardImportResponse;
  creditCardScanResult: ReadCreditCardStatementResponse | null = null;
  currentStatementFile: File | null = null;
  isCreditCardImportInProgress: boolean = false;
  isCreditCardScanInProgress: boolean = false;
  isScanModalDismissed: boolean = false;
  isImportModalDismissed: boolean = false;
  importingTransactionCount: number = 0;
  isImportSubmitting: boolean = false;
  importProgress: number = 0;
  private importProgressInterval: any;
  isAssigningPhase: boolean = false;
  currentAssigningCard: string = '';
  currentAssigningFullName: string = '';
  currentAssigningCardDisplay: string = '';
  private assigningCardInterval: any;
  private assigningCardIndex: number = 0;
  private uniqueCardNumbers: string[] = [];
  private claimantCardMap: Map<string, { fullName: string; cardDisplay: string }> = new Map();
  private assigningEntries: { fullName: string; cardDisplay: string }[] = [];
  alertPanelModel = new AlertPanelModel(
    false,
    "",
    [""],
    "",
    "",
    true,
    this.translate.instant("please_review_or_confirm")
  );

  alertPanelModelForDuplicateFile = new AlertPanelModel(
    false,
    "",
    [""],
    "",
    "",
    true,
    this.translate.instant("please_review_or_confirm")
  );

  alertPanelModelForReload = new AlertPanelModel(
    false,
    "",
    [""],
    "",
    "",
    true,
    this.translate.instant("Alert")
  );

  progressbarValue: number = 0;
  cardImportTemplateData: any;
  ccImportTemplateFormModel = new CardImportTemplateSaveRequest();

  ngOnInit(): void {
    // Wait for Tidio to load
    const waitForTidio = setInterval(() => {
      if (window.tidioChatApi) {
        clearInterval(waitForTidio);

        // Initial route check
        this.checkRoute(this.router.url);

        // Listen for route changes
        this.router.events
          .pipe(filter((event) => event instanceof NavigationEnd))
          .subscribe((event: NavigationEnd) => {
            this.checkRoute(event.urlAfterRedirects);
          });
      }
    }, 300);
  }

  private checkRoute(url: string): void {
    this.upToDateService.pollForBuildNumber().subscribe((buildIsUpToDate) => {
      this.hideOnRoutes = buildIsUpToDate.showChatbotOnRoutes;

      if (!url.startsWith("/claimant/expenselistdetails")) {
        const shouldHide = this.hideOnRoutes.some((route) =>
          url == "/" ? true : url != "/" && url.startsWith(route)
        );
        shouldHide ? window.tidioChatApi.show() : window.tidioChatApi.hide();
      } else {
        window.tidioChatApi.hide();
      }
    });
  }

  constructor(
    private translate: TranslateService,
    private router: Router,
    private oauthService: OAuthService,
    private alertService: AlertService,
    private upToDateService: UpToDateBuildService,
    private toastr: ToastrService,
    private creditCardService: CreditCardService,
    private claimsService: ClaimsService,
    private datePipe: DatePipe,
    private swUpdate: SwUpdate
  ) {
    this.claimsService.currentClaims.subscribe((claims) => {
      this.claims = claims;
    });

    this.claimsService.currentIsCreditCardScanInProgress.subscribe(
      (isCreditCardScanInProgress) => {
        this.isCreditCardScanInProgress = isCreditCardScanInProgress;
      }
    );

    this.claimsService.currentIsScanModalDismissed.subscribe((dismissed) => {
      this.isScanModalDismissed = dismissed;
    });

    this.claimsService.currentIsImportModalDismissed.subscribe((dismissed) => {
      this.isImportModalDismissed = dismissed;
    });

    this.claimsService.currentIsCreditCardImportInProgress.subscribe(
      (isCreditCardImportInProgress) => {
        this.isCreditCardImportInProgress = isCreditCardImportInProgress;
      }
    );

    this.claimsService.currentIsAwaitingAmountDirectionConfirmation.subscribe(
      (isAwaiting) => {
        if (isAwaiting && !this.amountDirectionConfirmed) {
          this.showAmountDirectionModal = true;
        }
      }
    );

    this.claimsService.currentdragnDropCardImportResponse.subscribe(
      (dragnDropCardImportResponse) => {
        this.dragnDropCardImportResponse = dragnDropCardImportResponse;

        if (this.dragnDropCardImportResponse) {

          this.progressbarValue =
            (this.dragnDropCardImportResponse.ImportingNow /
              this.dragnDropCardImportResponse.TotalTransactions) *
            100;
        }
      }
    );

    // Service Worker update detection via Angular's SwUpdate
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.showAppUpdatePanel = true;
        });

      this.swUpdate.unrecoverable.subscribe(() => {
        location.reload();
      });

      interval(60000).pipe(
        switchMap(() => this.swUpdate.checkForUpdate())
      ).subscribe();
    }

    // Fallback: build-details polling
    setInterval(() => {
      upToDateService.pollForBuildNumber().subscribe((buildIsUpToDate) => {
        if (
          this.upToDateService.buildNumberAtStartup !==
          buildIsUpToDate.buildNumber
        )
          this.showAppUpdatePanel = true;
      });
    }, 60000);

    this.translate.use(this.translate.getBrowserLang());

    this.oauthService.configure(authPasswordFlowConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.loadDiscoveryDocument();

    this.alertService.getFormClosingEvent().subscribe((hasChanges) => {
      if (hasChanges) {
        this.alertPanelModel = new AlertPanelModel(
          true,
          this.translate.instant("label_confirm"),
          [this.translate.instant("business_purpose_discard_msg")],
          this.translate.instant("label_cancel"),
          this.translate.instant("label_confirm"),
          true,
          this.translate.instant("please_review_or_confirm")
        );
      } else {
        this.alertPanelModel = new AlertPanelModel(
          false,
          "",
          [""],
          "",
          "",
          true,
          this.translate.instant("please_review_or_confirm")
        );
      }
    });
  }

  closeAlertPanel(returnValue) {
    this.alertPanelModel = new AlertPanelModel(
      false,
      "",
      [""],
      "",
      "",
      true,
      this.translate.instant("please_review_or_confirm")
    );
    this.alertService.onUserAction(returnValue);
  }

  closeAlertPanelForDuplicateFile(returnValue) {
    this.alertPanelModelForDuplicateFile = new AlertPanelModel(
      false,
      "",
      [""],
      "",
      "",
      true,
      this.translate.instant("please_review_or_confirm")
    );
    this.alertService.onUserAction(returnValue);
  }

  closeAlertPanelForReload(returnValue) {
    this.alertPanelModelForReload = new AlertPanelModel(
      false,
      "",
      [""],
      "",
      "",
      true,
      this.translate.instant("please_review_or_confirm")
    );
    if (returnValue) {
      if (this.swUpdate.isEnabled) {
        this.swUpdate.activateUpdate()
          .then(() => location.reload())
          .catch(() => location.reload());
      } else {
        location.reload();
      }
    }
  }
  onRefreshClick() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.activateUpdate()
        .then(() => window.location.reload())
        .catch(() => window.location.reload());
    } else {
      window.location.reload();
    }
  }

  showAppUpdatePanel = false;
  amountDirectionConfirmed = false;
  onStatementImport(files: FileList): void {
    const file = files[0];
    this.currentStatementFile = file;
    this.amountDirectionConfirmed = false;

    this.isCreditCardScanInProgress = true;
    this.isScanModalDismissed = false;
    this.claimsService.updateIsScanModalDismissed(false);
    this.claimsService.updateIsCreditCardScanInProgress(true);

    this.creditCardService
      .getStatementDataFromFile(file)
      .subscribe((initialScanResult: ReadCreditCardStatementResponse) => {
        this.isCreditCardScanInProgress = false;
        this.claimsService.updateIsCreditCardScanInProgress(false);
        this.claimsService.updateIsScanModalDismissed(false);

        if (initialScanResult.isFileAlreadyImported) {
          this.alertPanelModelForDuplicateFile = new AlertPanelModel(
            true,
            this.translate.instant("label_alert"),
            [
              this.translate.instant("this_file_was_imported_on") + " " +
              this.datePipe.transform(initialScanResult.importDateTime, "dd-MMM-yyyy hh:mm:ss a") + " " +
              this.translate.instant("with") + " " +
              initialScanResult.transactionsImported + " " +
              this.translate.instant("transactions_and_cannot_be_imported_again"),
            ],
            "",
            this.translate.instant("label_ok"),
            false,
            ""
          );
        } else if (initialScanResult.fileHashResponse?.success) {
          const transactions = initialScanResult.fileHashResponse.transactions ?? [];
          if (transactions.length === 0) {
            this.alertPanelModelForDuplicateFile = new AlertPanelModel(
              true,
              this.translate.instant("label_alert"),
              [this.translate.instant("cc_import_statement_no_transactions_in_file")],
              "",
              this.translate.instant("label_ok"),
              false,
              ""
            );
          } else {
            this.creditCardScanResult = initialScanResult;
            if (this.creditCardScanResult.fileHashResponse?.transactions) {
              const fallbackCurrency = this.claims?.UserHomeCurrencyCode;
              this.creditCardScanResult.fileHashResponse.transactions.forEach(t => {
                if (!t.currency && fallbackCurrency) {
                  t.currency = fallbackCurrency;
                }
                else if (t.currency && !t.exchangeRates) {
                  t.currency = this.claims?.UserHomeCurrencyCode;
                }
              });
            }
            this.fnShowStatementDateModal(initialScanResult.fileHashResponse.statementDate ?? null);
          }
        } else {
          this.alertPanelModelForDuplicateFile = new AlertPanelModel(
            true,
            this.translate.instant("label_alert"),
            [this.translate.instant("failed")],
            "",
            this.translate.instant("label_ok"),
            false,
            ""
          );
        }
      }, () => {
        this.isCreditCardScanInProgress = false;
        this.claimsService.updateIsCreditCardScanInProgress(false);
        this.claimsService.updateIsScanModalDismissed(false);
        this.alertPanelModelForDuplicateFile = new AlertPanelModel(
          true,
          this.translate.instant("label_alert"),
          [this.translate.instant("cc_import_statement_read_file_error")],
          "",
          this.translate.instant("label_ok"),
          false,
          ""
        );
      });
  }

  // ── Statement Date Confirmation Flow ─────────────────────────────────────
  stateDateStep: 'none' | 'confirm' | 'amend' | 'confirmed' | 'not-detected' = 'none';
  detectedStatementDate: Date | null = null;
  selectedCalendarDate: Date | null = null;
  calendarViewDate: Date = new Date();
  calendarWeeks: (Date | null)[][] = [];
  readonly calendarDayNames = ['Sun', 'Mo', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  private fnParseStatementDate(statementDate: string | null): Date | null {
    if (!statementDate) return null;
    // Day-first numeric: dd/MM/yyyy, dd-MM-yyyy, dd.MM.yyyy, d/M/yyyy, etc.
    // (matches this API's date convention elsewhere, e.g. transaction expenseDate "31/05/2026")
    const dayFirst = statementDate.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
    if (dayFirst) {
      const d = new Date(+dayFirst[3], +dayFirst[2] - 1, +dayFirst[1]);
      return isNaN(d.getTime()) ? null : d;
    }
    // Year-first numeric: yyyy-MM-dd, yyyy/MM/dd, yyyy.MM.dd - handled explicitly so
    // parsing doesn't depend on inconsistent browser support for non-dash separators.
    const yearFirst = statementDate.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/);
    if (yearFirst) {
      const d = new Date(+yearFirst[1], +yearFirst[2] - 1, +yearFirst[3]);
      return isNaN(d.getTime()) ? null : d;
    }
    // Fallback: ISO with time/timezone, textual month formats (e.g. "30 Jun 2026",
    // "June 30, 2026"), or any other browser-parseable format.
    const d = new Date(statementDate);
    return isNaN(d.getTime()) ? null : d;
  }

  fnShowStatementDateModal(statementDate: string | null): void {
    const parsed = this.fnParseStatementDate(statementDate);
    const isValid = parsed !== null;

    if (isValid) {
      this.detectedStatementDate = parsed;
      this.selectedCalendarDate = new Date(parsed);
      this.calendarViewDate = new Date(parsed);
      this.stateDateStep = 'confirm';
    } else {
      this.detectedStatementDate = null;
      this.selectedCalendarDate = null;
      this.calendarViewDate = new Date();
      this.fnBuildCalendar();
      this.stateDateStep = 'not-detected';
    }
  }

  fnConfirmStatementDate(): void {
    this.stateDateStep = 'confirmed';
    setTimeout(() => {
      this.stateDateStep = 'none';
      this.fnDetectAmountDirection();
      this.showAmountDirectionModal = true;
    }, 2500);
  }

  fnOpenAmendCalendar(): void {
    const isValidDate = (d: any) => d instanceof Date && !isNaN(d.getTime());
    const base = isValidDate(this.detectedStatementDate) ? this.detectedStatementDate : null;
    this.calendarViewDate = base ? new Date(base) : new Date();
    this.selectedCalendarDate = base ? new Date(base) : null;
    this.fnBuildCalendar();
    this.stateDateStep = 'amend';
  }

  fnSaveStatementDate(): void {
    if (!this.selectedCalendarDate) return;
    this.detectedStatementDate = new Date(this.selectedCalendarDate);
    this.stateDateStep = 'confirmed';
    setTimeout(() => {
      this.stateDateStep = 'none';
      this.fnDetectAmountDirection();
      this.showAmountDirectionModal = true;
    }, 2500);
  }

  fnCancelAmend(): void {
    this.stateDateStep = 'confirm';
  }

  fnResetToOriginal(): void {
    if (this.detectedStatementDate) {
      this.selectedCalendarDate = new Date(this.detectedStatementDate);
      this.calendarViewDate = new Date(this.detectedStatementDate);
      this.fnBuildCalendar();
    }
  }

  fnPrevMonth(): void {
    this.calendarViewDate = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth() - 1, 1);
    this.fnBuildCalendar();
  }

  fnNextMonth(): void {
    this.calendarViewDate = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth() + 1, 1);
    this.fnBuildCalendar();
  }

  fnBuildCalendar(): void {
    const viewDate = (this.calendarViewDate instanceof Date && !isNaN(this.calendarViewDate.getTime()))
      ? this.calendarViewDate
      : new Date();
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const weeks: (Date | null)[][] = [];
    let week: (Date | null)[] = Array(firstDay).fill(null);

    for (let d = 1; d <= daysInMonth; d++) {
      week.push(new Date(year, month, d));
      if (week.length === 7) { weeks.push(week); week = []; }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }
    this.calendarWeeks = weeks;
  }

  fnIsFutureDay(date: Date | null): boolean {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  }

  fnSelectCalendarDate(date: Date | null): void {
    if (!date || this.fnIsFutureDay(date)) return;
    this.selectedCalendarDate = date;
  }

  fnIsSameDay(a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  get calendarMonthLabel(): string {
    return this.datePipe.transform(this.calendarViewDate, 'MMMM yyyy') || '';
  }

  get isAmendDateChanged(): boolean {
    if (!this.selectedCalendarDate) return false;
    if (!this.detectedStatementDate) return true;
    return !this.fnIsSameDay(this.detectedStatementDate, this.selectedCalendarDate);
  }
  // ─────────────────────────────────────────────────────────────────────────

  // ── Amount Direction Confirmation Modal ──────────────────────────────────
  showAmountDirectionModal = false;
  /** 0 = smart detection auto-selected, 1 = negative=expenses, 2 = positive=expenses */
  amountDirectionOption: 0 | 1 | 2 = 1;
  amtDirMajorityNegative = true;
  rememberAmountDirectionChoice = true;

  fnDetectAmountDirection(): void {
    const txns = this.creditCardScanResult?.fileHashResponse?.transactions ?? [];
    const negativeCount = txns.filter(t => parseFloat(t.amount) < 0).length;
    this.amtDirMajorityNegative = negativeCount >= (txns.length - negativeCount);

    const savedRemember = localStorage.getItem('eod_amtdir_remember');
    const savedChoice = localStorage.getItem('eod_amtdir_choice');
    if (savedRemember === 'true' && savedChoice) {
      this.rememberAmountDirectionChoice = true;
      this.amountDirectionOption = parseInt(savedChoice, 10) as 0 | 1 | 2;
    } else {
      this.rememberAmountDirectionChoice = true;
      this.amountDirectionOption = this.amtDirMajorityNegative ? 1 : 2;
    }
  }

  fnDismissScanModal(): void {
    this.isScanModalDismissed = true;
    this.claimsService.updateIsScanModalDismissed(true);
  }

  fnReopenScanModal(): void {
    this.isScanModalDismissed = false;
    this.claimsService.updateIsScanModalDismissed(false);
  }

  fnDismissImportModal(): void {
    this.isImportModalDismissed = true;
    this.claimsService.updateIsImportModalDismissed(true);
  }

  fnReopenImportModal(): void {
    this.isImportModalDismissed = false;
    this.claimsService.updateIsImportModalDismissed(false);
  }

  fnStartAssigningPhase(transactions: any[]): void {
    const seen = new Set<string>();
    this.uniqueCardNumbers = transactions
      .filter(t => t.cardNumber ? !seen.has(t.cardNumber) && seen.add(t.cardNumber) : false)
      .map(t => t.cardNumber);

    if (this.uniqueCardNumbers.length === 0) return;

    // Fetch claimant–card assignments and build last-4 lookup map
    this.creditCardService.GetAssignCreditCardToClaimantList(this.claims.OrgId)
      .subscribe({
        next: (res: any) => {
          this.claimantCardMap = new Map();
          const claimants: any[] = res?.responseInfo?.claimantList ?? [];
          for (const claimant of claimants) {
            for (const card of (claimant.assignCardList ?? [])) {
              if (card.cardNumber) {
                const last4 = String(card.cardNumber).slice(-4);
                this.claimantCardMap.set(last4, {
                  fullName: claimant.fullName ?? '',
                  cardDisplay: String(card.cardNumber).padStart(16, 'X'),
                });
              }
            }
          }
          this.fnStartCardCycling();
        },
        error: () => this.fnStartCardCycling(),
      });
  }

  private fnStartCardCycling(): void {
    this.assigningEntries = this.uniqueCardNumbers
      .map(cardNum => {
        const last4 = String(cardNum ?? '').slice(-4);
        const match = this.claimantCardMap.get(last4);
        return match ? { fullName: match.fullName, cardDisplay: match.cardDisplay } : null;
      })
      .filter((entry): entry is { fullName: string; cardDisplay: string } => entry !== null);

    if (this.assigningEntries.length === 0) return;

    this.isAssigningPhase = true;
    this.claimsService.updateIsAssigningPhase(true);

    this.assigningCardIndex = 0;
    this.currentAssigningFullName = this.assigningEntries[0].fullName;
    this.currentAssigningCardDisplay = this.assigningEntries[0].cardDisplay;
    this.currentAssigningCard = this.currentAssigningFullName;

    this.assigningCardInterval = setInterval(() => {
      this.assigningCardIndex = (this.assigningCardIndex + 1) % this.assigningEntries.length;
      const entry = this.assigningEntries[this.assigningCardIndex];
      this.currentAssigningFullName = entry.fullName;
      this.currentAssigningCardDisplay = entry.cardDisplay;
      this.currentAssigningCard = this.currentAssigningFullName;
    }, 900);
  }

  fnStopAssigningPhase(): void {
    if (this.assigningCardInterval) {
      clearInterval(this.assigningCardInterval);
      this.assigningCardInterval = null;
    }
    this.isAssigningPhase = false;
    this.claimsService.updateIsAssigningPhase(false);
    this.currentAssigningCard = '';
    this.currentAssigningFullName = '';
    this.currentAssigningCardDisplay = '';
    this.uniqueCardNumbers = [];
    this.assigningEntries = [];
  }

  fnConfirmAmountDirectionAndImport(): void {
    if (!this.currentStatementFile || !this.creditCardScanResult?.fileHashResponse) return;

    if (this.rememberAmountDirectionChoice) {
      localStorage.setItem('eod_amtdir_remember', 'true');
      localStorage.setItem('eod_amtdir_choice', String(this.amountDirectionOption));
    } else {
      localStorage.removeItem('eod_amtdir_remember');
      localStorage.removeItem('eod_amtdir_choice');
    }

    this.amountDirectionConfirmed = true;
    this.showAmountDirectionModal = false;
    this.claimsService.updateIsAwaitingAmountDirectionConfirmation(false);

    this.creditCardScanResult.fileHashResponse.statementDate = this.selectedCalendarDate
      ? this.datePipe.transform(this.selectedCalendarDate, 'dd/MM/yyyy')
      : null;
    debugger;

    const txns = this.creditCardScanResult.fileHashResponse.transactions ?? [];
    const selectedTransactions = txns.map(t => ({
      ...t,
      expenseDate: this.fnParseDDMMYYYYToISO(t.expenseDate),
      isExpense: t.isPaymentToCard
        ? false
        : this.amountDirectionOption === 1
          ? parseFloat(t.amount) < 0
          : parseFloat(t.amount) > 0,
      selected: true,
    }));

    this.reviewSelectedCount = selectedTransactions.length;

    const formData = new FormData();
    formData.append('file', this.currentStatementFile);
    formData.append('fileScanResponseWithFileHash', JSON.stringify(this.creditCardScanResult));
    formData.append('transactions', JSON.stringify(selectedTransactions));

    this.isImportModalDismissed = false;
    this.isImportSubmitting = true;
    this.importingTransactionCount = selectedTransactions.length;
    this.claimsService.updateIsImportModalDismissed(false);
    this.claimsService.updateIsImportSubmitting(true);
    this.claimsService.updateImportingTransactionCount(selectedTransactions.length);
    this.importProgress = 0;
    this.claimsService.updateImportProgress(0);
    let assigningStarted = false;
    const progressStep = Math.max(0.015, Math.min(0.12, 1.2 / selectedTransactions.length));
    this.importProgressInterval = setInterval(() => {
      this.importProgress += (92 - this.importProgress) * progressStep;
      this.claimsService.updateImportProgress(this.importProgress);
      if (!assigningStarted && this.importProgress > 45) {
        assigningStarted = true;
        this.fnStartAssigningPhase(selectedTransactions);
      }
    }, 300);

    this.creditCardService.importCreditCardStatement(formData).subscribe({
      next: () => {
        clearInterval(this.importProgressInterval);
        this.fnStopAssigningPhase();
        this.importProgress = 100;
        this.claimsService.updateImportProgress(100);
        setTimeout(() => {
          this.isImportSubmitting = false;
          this.importProgress = 0;
          this.claimsService.updateIsImportSubmitting(false);
          this.claimsService.updateImportProgress(0);
        }, 400);
        this.toastr.success(this.translate.instant("success"));        
      },
      error: () => {
        clearInterval(this.importProgressInterval);
        this.fnStopAssigningPhase();
        this.importProgress = 0;
        this.isImportSubmitting = false;
        this.claimsService.updateIsImportSubmitting(false);
        this.claimsService.updateImportProgress(0);
        this.alertPanelModelForDuplicateFile = new AlertPanelModel(
          true,
          this.translate.instant('label_alert'),
          [this.translate.instant('failed')],
          '',
          this.translate.instant('label_ok'),
          false,
          ''
        );
      },
    });
  }

  fnReviewManually(): void {
    this.amountDirectionConfirmed = true;
    this.showAmountDirectionModal = false;
    this.claimsService.updateIsAwaitingAmountDirectionConfirmation(false);
    this.fnOpenReviewManuallyModal();
  }

  // ── Review Manually Side Panel ────────────────────────────────────────────
  showReviewManuallyModal = false;
  reviewTransactions: Array<CreditCardStatementTransaction & { isExpense: boolean }> = [];
  reviewFilteredTransactions: Array<CreditCardStatementTransaction & { isExpense: boolean }> = [];
  reviewSelectedCount = 0;
  reviewGridColumnDefs: ColDef[] = [];
  reviewGridConfigItems: GridConfig;
  reviewTypeDropdown = [
    { dropdownName: 'All (0)', filterKey: 'All' },
    { dropdownName: 'Expense (0)', filterKey: 'Expense' },
    { dropdownName: 'Payments / Refunds (0)', filterKey: 'Payments / Refunds' },
  ];

  fnUpdateReviewTypeDropdownCounts(): void {
    const total = this.reviewTransactions.length;
    const expenseCount = this.reviewTransactions.filter(t => t.isExpense).length;
    const paymentsCount = total - expenseCount;

    // Capture current selection by stable filterKey before replacing the array
    const currentLabel = this.reviewGridRef?.selectedFilter ?? 'All';
    const currentKey =
      this.reviewTypeDropdown.find(d => d.dropdownName === currentLabel)?.filterKey
      ?? this.reviewTypeDropdown.find(d => d.filterKey === currentLabel)?.filterKey
      ?? 'All';

    // Replace array (not mutate in-place) so ng-select detects the change and re-renders options
    this.reviewTypeDropdown = [
      { dropdownName: `All (${total})`, filterKey: 'All' },
      { dropdownName: `Expense (${expenseCount})`, filterKey: 'Expense' },
      { dropdownName: `Payments / Refunds (${paymentsCount})`, filterKey: 'Payments / Refunds' },
    ];

    // Restore selectedFilter to the updated label so ng-select displays correctly
    if (this.reviewGridRef) {
      const newLabel = this.reviewTypeDropdown.find(d => d.filterKey === currentKey)?.dropdownName;
      if (newLabel) {
        this.reviewGridRef.selectedFilter = newLabel;
      }
    }
  }

  fnOpenReviewManuallyModal(): void {
    const txns = this.creditCardScanResult?.fileHashResponse?.transactions ?? [];
    this.reviewTransactions = txns.map(t => ({
      ...t,
      expenseDate: this.fnParseDDMMYYYYToISO(t.expenseDate),
      isExpense: t.isPaymentToCard
        ? false
        : this.amountDirectionOption === 1
          ? parseFloat(t.amount) < 0
          : parseFloat(t.amount) > 0,
      selected: false,
    }));
    this.reviewFilteredTransactions = [...this.reviewTransactions];
    this.reviewSelectedCount = 0;
    this.fnUpdateReviewTypeDropdownCounts();
    this.reviewGridColumnDefs = [
      {
        headerName: 'Card Number',
        field: 'cardNumber',
        suppressHeaderMenuButton: true,
        minWidth: 140,
      },
      {
        headerName: 'Expense Date',
        field: 'expenseDate',
        suppressHeaderMenuButton: true,
        minWidth: 120,
        cellRenderer: 'dateRenderer',
      },
      {
        headerName: 'Supplier',
        field: 'supplier',
        suppressHeaderMenuButton: true,
        minWidth: 180,
      },
      {
        headerName: 'Amount',
        field: 'amount',
        suppressHeaderMenuButton: true,
        minWidth: 120,
        headerClass: 'text-right',
        cellClass: 'text-right',
        cellRenderer: (params: any) => {
          if (!params.data) return '';
          const color = params.data.isExpense ? '#e5425a' : '#16a34a';
          return `<span style="color:${color};font-weight:600">${params.data.amount}</span>`;
        },
      },
      {
        headerName: '',
        field: 'isExpense',
        suppressHeaderMenuButton: true,
        sortable: false,
        minWidth: 170,
        cellRenderer: (params: any) => {
          if (!params.data) return '';
          const isExp = params.data.isExpense;
          const bg = isExp ? '#fff0f2' : '#f0fdf4';
          const color = isExp ? '#e5425a' : '#16a34a';
          const dotBg = isExp ? '#e5425a' : '#16a34a';
          const label = isExp ? 'Expense' : 'Payments / Refunds';
          return `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;border-radius:20px;padding:2px 8px;max-height:26px;background:${bg};color:${color}"><span style="width:6px;height:6px;border-radius:50%;background:${dotBg};display:inline-block;flex-shrink:0"></span>${label}</span>`;
        },
      },
      {
        headerName: '',
        field: 'toggleType',
        suppressHeaderMenuButton: true,
        sortable: false,
        minWidth: 60,
        maxWidth: 60,
        cellRenderer: () => `<button style="background:none;border-radius:6px;padding:5px;cursor:pointer;line-height:1;display:inline-flex;align-items:center;justify-content:center" title="Toggle type"><img src="${ICONS.REPEAT}" style="width:14px;height:14px" /></button>`,
      },
    ];
    this.reviewGridConfigItems = new GridConfig(
      false,  // hideFilters
      true,   // isInsidePanel
      'expenseDate',  // defaultSortColumn
      'uniqueRowId',  // rowKey
      0,      // statusValue
      true,   // enableRowSelection
      [],     // countByStatus
      false,  // suppressCellSelection
      false,  // suppressRowClickSelection
      true,   // isCheckAllAllowed
      '',     // gridTitle
      'asc',  // defaultSortOrder
      32,     // rowHeight
      'no_data_availabe', // noDataMessage
      null,   // headerHeight
      null,   // rowClassRules
      false,  // isHoverRowColumnHighlight
      false,  // suppressRowHoverHighlight
      '',     // searchBarClass
      '',     // gridWrapperClass
      false   // columnHoverHighlight
    );
    this.showReviewManuallyModal = true;
    setTimeout(() => {
      if (this.reviewGridRef) {
        this.reviewGridRef.refreshData();
        // Sync selectedFilter now that the grid has rendered (first open)
        const allItem = this.reviewTypeDropdown.find(d => d.filterKey === 'All');
        if (allItem) {
          this.reviewGridRef.selectedFilter = allItem.dropdownName;
        }
      }
    }, 100);
  }

  private fnSyncReviewFiltersVisibility(): void {
    if (this.reviewGridConfigItems) {
      this.reviewGridConfigItems.hideFilters = this.reviewSelectedCount > 0;
    }
  }

  fnReviewTypeFilterChange(item: any): void {
    const value = item?.filterKey ?? item?.dropdownName ?? item ?? 'All';
    if (value === 'Expense') {
      this.reviewFilteredTransactions = this.reviewTransactions.filter(t => t.isExpense);
    } else if (value === 'Payments / Refunds') {
      this.reviewFilteredTransactions = this.reviewTransactions.filter(t => !t.isExpense);
    } else {
      this.reviewFilteredTransactions = [...this.reviewTransactions];
    }
  }

  fnReviewCellClick(params: any): void {
    if (params.column?.colId === 'selected') {
      this.reviewSelectedCount = this.reviewGridRef?.allGridData?.filter((r: any) => r.selected).length ?? 0;
      this.fnSyncReviewFiltersVisibility();
    } else if (params.column?.colId === 'toggleType') {
      params.data.isExpense = !params.data.isExpense;
      this.fnUpdateReviewTypeDropdownCounts();
      // Re-apply the active filter so toggled rows move in/out of the filtered view
      const currentLabel = this.reviewGridRef?.selectedFilter ?? 'All';
      const currentItem = this.reviewTypeDropdown.find(d => d.dropdownName === currentLabel)
        ?? this.reviewTypeDropdown.find(d => d.filterKey === currentLabel);
      this.fnReviewTypeFilterChange(currentItem ?? currentLabel);
      this.reviewGridRef?.refreshGridData();
    }
  }

  fnReviewOnCheckAll(isChecked: boolean): void {
    this.reviewSelectedCount = isChecked ? (this.reviewGridRef?.allGridData?.length ?? 0) : 0;
    this.fnSyncReviewFiltersVisibility();
  }

  fnReviewMarkSelected(isExpense: boolean): void {
    const allData = this.reviewGridRef?.allGridData ?? [];
    allData.forEach((r: any) => {
      if (r.selected) {
        r.isExpense = isExpense;
        r.selected = false;
      }
    });
    this.reviewSelectedCount = 0;
    this.fnUpdateReviewTypeDropdownCounts();
    this.fnSyncReviewFiltersVisibility();
    this.reviewGridRef?.refreshGridData();
  }

  fnReviewDeselectAll(): void {
    this.reviewGridRef?.onAllChecked(false);
    this.reviewSelectedCount = 0;
    this.fnSyncReviewFiltersVisibility();
    this.reviewGridRef?.refreshGridData();
  }

  fnParseDDMMYYYYToISO(dateStr: string): string {
    if (!dateStr) return dateStr;
    // Handles dd/mm/yyyy or dd-mm-yyyy
    const parts = dateStr.split(/[\/\-]/);
    if (parts.length === 3 && parts[0].length <= 2 && parts[2].length === 4) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  }

  fnReviewGoBack(): void {
    this.showReviewManuallyModal = false;
    this.showAmountDirectionModal = true;
  }

  fnReviewConfirmImport(): void {
    debugger;
    if (!this.currentStatementFile || !this.creditCardScanResult?.fileHashResponse) return;

    this.creditCardScanResult.fileHashResponse.statementDate = this.selectedCalendarDate ? this.datePipe.transform(this.selectedCalendarDate, 'dd/MM/yyyy') : null;

    const allGridData: any[] = this.reviewTransactions;
    const selectedTransactions = allGridData;
    const formData = new FormData();
    formData.append('file', this.currentStatementFile);
    formData.append('fileScanResponseWithFileHash', JSON.stringify(this.creditCardScanResult));
    formData.append('transactions', JSON.stringify(selectedTransactions));

    this.showReviewManuallyModal = false;
    this.isImportModalDismissed = false;
    this.isImportSubmitting = true;
    this.importingTransactionCount = selectedTransactions.length;
    this.claimsService.updateIsImportModalDismissed(false);
    this.claimsService.updateIsImportSubmitting(true);
    this.claimsService.updateImportingTransactionCount(selectedTransactions.length);
    this.importProgress = 0;
    this.claimsService.updateImportProgress(0);
    let assigningStarted = false;
    const progressStep = Math.max(0.015, Math.min(0.12, 1.2 / selectedTransactions.length));
    this.importProgressInterval = setInterval(() => {
      this.importProgress += (92 - this.importProgress) * progressStep;
      this.claimsService.updateImportProgress(this.importProgress);
      if (!assigningStarted && this.importProgress > 45) {
        assigningStarted = true;
        this.fnStartAssigningPhase(selectedTransactions);
      }
    }, 300);

    this.creditCardService.importCreditCardStatement(formData).subscribe({
      next: () => {
        clearInterval(this.importProgressInterval);
        this.fnStopAssigningPhase();
        this.importProgress = 100;
        this.claimsService.updateImportProgress(100);
        setTimeout(() => {
          this.isImportSubmitting = false;
          this.importProgress = 0;
          this.claimsService.updateIsImportSubmitting(false);
          this.claimsService.updateImportProgress(0);
        }, 400);        
        this.toastr.success(this.translate.instant("success"));        
      },
      error: () => {
        clearInterval(this.importProgressInterval);
        this.fnStopAssigningPhase();
        this.importProgress = 0;
        this.isImportSubmitting = false;
        this.claimsService.updateIsImportSubmitting(false);
        this.claimsService.updateImportProgress(0);
        this.alertPanelModelForDuplicateFile = new AlertPanelModel(
          true,
          this.translate.instant('label_alert'),
          [this.translate.instant('failed')],
          '',
          this.translate.instant('label_ok'),
          false,
          ''
        );
      },
    });
  }
  // ──────────────────────────────────────────────────────────────────────────

  isDragging = false;
  onDragging(state: boolean) {
    this.isDragging = state;
  }

  droppedFiles: File[] = [];

  onFilesDropped(files: FileList) {
    console.log("📁 Files dropped:", files);
    if (!this.claims?.IsFinanceManager) return;
    localStorage.setItem('cc_drag_drop_used_' + this.claims?.OrgId + '_' + this.claims?.UserId, '1');

    if (!this.claims.IsCreditCardPackageSubscribed) {
      this.alertPanelModelForDuplicateFile = new AlertPanelModel(
        true,
        this.translate.instant("label_alert"),
        [this.translate.instant("drag_and_drop_cc_not_subscribed")],
        "",
        this.translate.instant("label_ok"),
        false,
        ""
      );
    } else if (this.claims.IsCreditCardPackageSubscribed && !this.claims.IsCardSetupComplete) {
      this.alertPanelModelForDuplicateFile = new AlertPanelModel(
        true,
        this.translate.instant("credit_card_setup_is_incomplete"),
        [],
        "",
        this.translate.instant("label_ok"),
        true,
        this.translate.instant("credit_card_setup_incomplete_note")
      );

    } else {
      if (
        files.length === 1 &&
        (files[0].type === "application/pdf" ||
          files[0].type === "text/csv" ||
          files[0].type === "application/vnd.ms-excel" || // .xls
          files[0].type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") // .xlsx
      ) {

        this.creditCardService.GetCardImportTemplate(this.claims.OrgId)
          .subscribe(response => {
            if (response) {
              this.cardImportTemplateData = response.responseInfo;
              if (this.cardImportTemplateData) {
                this.onStatementImport(files);
              }
              else {
                this.ccImportTemplateFormModel = new CardImportTemplateSaveRequest();
                this.ccImportTemplateFormModel.dateFormat = 0;
                this.ccImportTemplateFormModel.expenseDateCol = 1;
                this.ccImportTemplateFormModel.cardNumberCol = 2;
                this.ccImportTemplateFormModel.amountCol = 3;
                this.ccImportTemplateFormModel.currencyCol = 4;
                this.ccImportTemplateFormModel.exchangeRateCol = 5;
                this.ccImportTemplateFormModel.localCurrencyAmountCol = 6;
                this.ccImportTemplateFormModel.supplierCol = 7;
                this.ccImportTemplateFormModel.drCrCol = 8;
                this.ccImportTemplateFormModel.notes1Col = 9;
                this.ccImportTemplateFormModel.notes2Col = 10;
                this.ccImportTemplateFormModel.notes3Col = 11;
                this.ccImportTemplateFormModel.notes4Col = 12;
                this.ccImportTemplateFormModel.referenceNumberCol = 13;
                this.ccImportTemplateFormModel.importDateFormat = 'dd-MMM-yyyy';

                let request = new ApiRequest<ICardImportTemplateSaveRequest>("SaveCardImportTemplate", environment.applicationVersion, environment.requestId);
                request.requestInfo = new CardImportTemplateSaveRequest();
                request.requestInfo = this.ccImportTemplateFormModel;
                request.requestInfo.organisationId = this.claims.OrgId;
                request.requestInfo.createdBy = this.claims.UserId;
                request.requestInfo.updatedBy = this.claims.UserId;
                request.requestInfo.createdByName = this.claims.FinanceManager;
                this.creditCardService.SaveCardImportTemplate(request)
                  .subscribe(response => {
                    if (response && response.statusCode == 0) {
                      this.onStatementImport(files);
                    }
                    else {
                      this.toastr.error(this.translate.instant("failed"));
                    }
                  });
              }
            }
          });
      } else {
        if (files.length === 1) {
          this.alertPanelModelForDuplicateFile = new AlertPanelModel(
            true,
            this.translate.instant("label_alert"),
            [this.translate.instant("drag_and_drop_unsupported_file")],
            "",
            this.translate.instant("label_ok"),
            false,
            ""
          );
        } else {
          this.alertPanelModelForDuplicateFile = new AlertPanelModel(
            true,
            this.translate.instant("label_alert"),
            [
              this.translate.instant(
                "drag_and_drop_imported_more_than_one_file"
              ),
            ],
            "",
            this.translate.instant("label_ok"),
            false,
            ""
          );
        }
      }
    }
  }

  griddata: any;
  closeCreditCardImportSummary() {
    this.isCreditCardImportInProgress = false;
    this.isCreditCardScanInProgress = false;
    this.claimsService.updateIsCreditCardScanInProgress(false);
    this.showDuplicateTransactionsTable = false;
    this.showMissingTransactionsTable = false;
  }

  showDuplicateTransactionsTable: boolean = false;

  showDuplicateTransactions() {
    this.showDuplicateTransactionsTable = true;
    this.showMissingTransactionsTable = false;
    this.griddata = this.dragnDropCardImportResponse?.ImportedData.filter(
      (item) => item.errors?.some((e) => e.ErrorCode === "INVCC10")
    );
  }

  showMissingTransactionsTable: boolean = false;

  showMissingTransactions() {
    this.showMissingTransactionsTable = true;
    this.showDuplicateTransactionsTable = false;
    this.griddata = this.dragnDropCardImportResponse?.ImportedData.filter(
      (item) => item.errors?.some((e) => e.ErrorCode === "INVCC01")
    );
  }

  ICONS = ICONS;
}
